import { useState } from 'react';
import { ChevronsUpDown } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Badge } from '@/components/ui/badge';
import { FormItem, FormControl, FormDescription } from '@/components/ui/form';

interface Item {
  value: string;
  label: string;
}

interface MultiSelectComboboxProps {
  items: Item[];
  selectedValues: string[];
  onSelectionChange: (values: string[]) => void;
  placeholder?: string;
  emptyMessage?: string;
  description: string;
  disabled: boolean;
  searchEnabled?: boolean;
  searchPlaceholder?: string;
}

export function MultiSelectCombobox({
  items,
  selectedValues,
  onSelectionChange,
  placeholder,
  description,
  disabled,
  searchEnabled,
  searchPlaceholder,
}: MultiSelectComboboxProps) {
  const [open, setOpen] = useState(false);

  const handleSelect = (value: string) => {
    const newValues = selectedValues.includes(value)
      ? selectedValues.filter((v) => v !== value)
      : [...selectedValues, value];
    onSelectionChange(newValues);
  };

  const selectedLabels = items
    .filter((item) => selectedValues.includes(item.value))
    .map((item) => item.label)
    .join(', ');

  const selectAllFields = () => {
    const isAllSelected = selectedValues.length === items.length;
    if (isAllSelected) {
      onSelectionChange([]);
    } else {
      const newValues = [...items.map((x) => x.value)];
      onSelectionChange(newValues);
    }
  };

  const isAllSelected =
    selectedValues.length > 0 && selectedValues.length === items.length;
  return (
    <FormItem>
      <FormControl>
        <Popover open={open} onOpenChange={setOpen} modal>
          <PopoverTrigger asChild disabled={disabled}>
            <Button
              variant='outline'
              role='combobox'
              aria-expanded={open}
              className='py-1 px-3 h-auto min-h-10'
            >
              <div className='flex justify-between items-center gap-0.5 flex-wrap'>
                {!selectedLabels && <p className='truncate'>{placeholder}</p>}
                {selectedLabels.length > 0 &&
                  selectedLabels.split(',').map((badge) => (
                    <Badge key={badge} className='py-1 mr-1'>
                      {badge}
                    </Badge>
                  ))}
              </div>
              <ChevronsUpDown className='ml-2 h-4 w-4 shrink-0 opacity-50' />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className='w-[171px] p-0'
            align='start'
            sideOffset={5}
          >
            <Command>
              {searchEnabled && (
                <CommandInput placeholder={searchPlaceholder} className='h-9' />
              )}
              <CommandList>
                <CommandGroup className='p-0'>
                  <CommandItem
                    value='Select all'
                    onSelect={() => {
                      selectAllFields();
                    }}
                    className='flex items-center gap-2 cursor-pointer'
                  >
                    <Checkbox
                      checked={isAllSelected}
                      className='pointer-events-none'
                    />
                    <span className='flex-1'>Select All</span>
                  </CommandItem>
                  <hr className='py-1' />
                  {items.map((item) => (
                    <CommandItem
                      key={item.value}
                      value={item.value}
                      onSelect={() => handleSelect(item.value)}
                      className='flex items-center gap-2 cursor-pointer'
                    >
                      <Checkbox
                        checked={selectedValues.includes(item.value)}
                        onCheckedChange={() => handleSelect(item.value)}
                        className='pointer-events-none'
                      />
                      <span className='flex-1'>{item.label}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </FormControl>
      <FormDescription>{description}</FormDescription>
    </FormItem>
  );
}
